// ============================================================
// TwoFactorPanel.tsx
// Settings → Account: enable / manage TOTP two-factor auth. Self-service
// enrolment only (backend Stage 1-2):
//   • get2faStatusApi   → { enabled }
//   • setup2faApi       → { qrDataUrl, secret, otpauthUri }  (secret stored, NOT active)
//   • confirm2faApi(code) → { enabled, backupCodes }  (activates + one-time codes)
//
// Flow when DISABLED: "Enable" → show QR + manual secret → enter 6-digit
// code → confirm → show the 10 backup codes ONCE (copy/download + a
// "you won't see these again" warning) → land on the enabled state.
//
// KNOWN GAP (flagged, not faked): there is NO backend DISABLE endpoint
// and NO admin-reset endpoint yet. So when enabled we show status only
// and explain how to turn it off, rather than wiring a button to a
// route that doesn't exist. See the report / the note in the enabled
// branch below.
// ============================================================
import React, { useEffect, useState } from 'react'
import { get2faStatusApi, setup2faApi, confirm2faApi, disable2faApi } from '../../api/casevoApi'
import {
  SettingsCard, SettingsToggleRow, SettingsBadge, SIcon, sInput,
  S_BTN_PRIMARY, S_BTN_PRIMARY_SM, S_BTN_GHOST, S_BTN_GHOST_SM, S_BTN_DANGER, S_BTN_DANGER_SM,
  S_FORMFOOT, S_LABEL, S_NOTE,
} from '../settings/settingsUi'
import ActionBox, { ErrorNote } from './ActionBox'

type Mode = 'idle' | 'enrolling' | 'codes'

interface TwoFactorPanelProps {
  // Fired once the user has completed enrolment AND acknowledged their
  // one-time backup codes (the "Done" button in the codes step). Optional
  // — the Settings usage passes nothing and the panel simply returns to
  // its idle "2FA is on" state. The forced-setup gate passes a handler
  // that refreshes getMe so the enforcement gate falls through. Only
  // fires on genuine completion, never on cancel, so a user can't slip
  // past enforcement without real, confirmed 2FA + saved codes.
  onComplete?: () => void
  // Presentation mode. Default (false) = the Settings look: a bordered
  // surface card with its own "Two-factor authentication" heading, all
  // content left-aligned in the settings column. When true (the forced-
  // setup gate), the panel drops its card chrome + own heading and
  // centres its inner elements so it sits cleanly under the standalone
  // screen's own centred heading — matching the LoginScreen aesthetic.
  // Purely visual; the enrolment logic is identical in both modes.
  standalone?: boolean
}

const TwoFactorPanel: React.FC<TwoFactorPanelProps> = ({ onComplete, standalone = false }) => {
  const [statusLoading, setStatusLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [mode, setMode] = useState<Mode>('idle')

  // Enrolment material (from setup).
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState('')

  // The one-time backup codes (shown only right after a successful confirm).
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [copied, setCopied] = useState(false)

  // When enabled: reveals the code prompt for turning 2FA off.
  const [disableMode, setDisableMode] = useState(false)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load current status on mount.
  useEffect(() => {
    let cancelled = false
    void get2faStatusApi()
      .then((res) => { if (!cancelled) setEnabled(res.enabled) })
      .catch(() => { /* leave disabled view; the Enable button still works */ })
      .finally(() => { if (!cancelled) setStatusLoading(false) })
    return () => { cancelled = true }
  }, [])

  const startEnrolment = async (): Promise<void> => {
    setError(null)
    setBusy(true)
    try {
      const res = await setup2faApi()
      setQrDataUrl(res.qrDataUrl)
      setSecret(res.secret)
      setCode('')
      setMode('enrolling')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start 2FA setup.')
    } finally {
      setBusy(false)
    }
  }

  const confirmEnrolment = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    if (!code.trim()) { setError('Enter the 6-digit code from your app.'); return }
    setBusy(true)
    try {
      const res = await confirm2faApi(code.trim())
      setBackupCodes(res.backupCodes)
      setEnabled(true)
      setMode('codes')
      // Clear enrolment material now that 2FA is live.
      setQrDataUrl(null)
      setSecret(null)
      setCode('')
    } catch (err) {
      // Wrong/expired code → stay on the step so they can retry.
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const cancelEnrolment = (): void => {
    setMode('idle')
    setQrDataUrl(null)
    setSecret(null)
    setCode('')
    setError(null)
  }

  const submitDisable = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    if (!code.trim()) { setError('Enter a current code to disable 2FA.'); return }
    setBusy(true)
    try {
      await disable2faApi(code.trim())
      setEnabled(false)
      setDisableMode(false)
      setCode('')
    } catch (err) {
      // Wrong/expired code → stay so they can retry.
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const copyCodes = (): void => {
    void navigator.clipboard?.writeText(backupCodes.join('\n')).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    }).catch(() => { /* clipboard blocked — the download affordance still works */ })
  }

  const downloadCodes = (): void => {
    const blob = new Blob(
      [`Casevo two-factor backup codes\n\nKeep these somewhere safe. Each code works once.\n\n${backupCodes.join('\n')}\n`],
      { type: 'text/plain' },
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'casevo-2fa-backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Settings: a bordered surface card. Standalone (forced gate): no card
  // chrome, centred content so it aligns under the screen's own heading.
  const cardClass = standalone
    ? 'text-center'
    : 'bg-casevo-surface border border-casevo-border rounded-lg p-6 mb-6'
  // Block-level elements (QR, inputs) don't inherit text-center, so they
  // need mx-auto to centre in standalone; empty string in Settings mode.
  const centerBlock = standalone ? 'mx-auto' : ''
  // Button rows are flex; centre them in standalone, left otherwise.
  const rowJustify = standalone ? 'justify-center' : ''
  const errorBanner = error && <ErrorNote className="mb-4 text-left">{error}</ErrorNote>

  // ── Settings mode (Option B redesign card) ──────────────────
  // The standalone forced-setup gate below keeps its original centred
  // markup untouched; Settings gets the mockup's card + rows.
  if (!standalone) {
    const settingsError = error && <ErrorNote className="mb-[12px]">{error}</ErrorNote>
    const codeInput = (
      <input
        autoFocus
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="123456"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="Verification code"
        disabled={busy}
        className={`${sInput()} max-w-[220px] tracking-[0.2em]`}
      />
    )
    return (
      <SettingsCard
        icon="shield"
        title="Two-factor authentication"
        sub="A second step at sign-in using an authenticator app."
        right={statusLoading ? null : (
          <SettingsBadge on={enabled} dot={enabled}>{enabled ? 'On' : 'Off'}</SettingsBadge>
        )}
      >
        {statusLoading ? (
          <div className="h-[54px] flex items-center"><div className="h-[14px] w-40 rounded bg-casevo-mint/60 animate-pulse" /></div>
        ) : mode === 'codes' ? (
          // ── One-time backup codes ──────────────────────────
          <div className="pt-[10px]">
            <ActionBox tone="warn" className="mb-[12px]" title="Save your backup codes now."
              message={<>Each code works once and lets you sign in if you lose your device. They will <strong className="font-semibold">not be shown again.</strong></>} />
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-[8px] font-mono text-[13.5px] text-casevo-forest">
              {backupCodes.map((c) => (
                <li key={c} className="px-[12px] py-[8px] bg-casevo-input border border-casevo-border rounded-[9px] text-center tracking-[0.15em]">
                  {c}
                </li>
              ))}
            </ul>
            <div className={S_FORMFOOT}>
              <button type="button" onClick={copyCodes} className={S_BTN_GHOST_SM}>
                <SIcon name="copy" size={14} />{copied ? 'Copied' : 'Copy codes'}
              </button>
              <button type="button" onClick={downloadCodes} className={S_BTN_GHOST_SM}>
                <SIcon name="upload" size={14} className="rotate-180" />Download .txt
              </button>
              <span className="flex-1" />
              <button type="button" onClick={() => { setMode('idle'); setBackupCodes([]); onComplete?.() }} className={S_BTN_PRIMARY_SM}>
                Done
              </button>
            </div>
          </div>
        ) : mode === 'enrolling' ? (
          // ── QR + confirm ───────────────────────────────────
          <form onSubmit={confirmEnrolment} className="pt-[10px]">
            {settingsError}
            <p className={`${S_NOTE} mt-[8px] mb-[12px]`}>
              Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy…), then
              enter the 6-digit code it shows.
            </p>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="2FA QR code" className="w-44 h-44 mb-[12px] rounded-[10px] border border-casevo-border bg-white p-2" />
            )}
            {secret && (
              <p className={`${S_NOTE} mb-[14px]`}>
                Can&apos;t scan? Enter this key manually:{' '}
                <span className="font-mono text-casevo-forest break-all">{secret}</span>
              </p>
            )}
            <label className={S_LABEL}>Verification code</label>
            {codeInput}
            <div className={S_FORMFOOT}>
              <button type="button" onClick={cancelEnrolment} disabled={busy} className={S_BTN_GHOST}>Cancel</button>
              <button type="submit" disabled={busy} className={S_BTN_PRIMARY}>{busy ? 'Confirming…' : 'Confirm'}</button>
            </div>
          </form>
        ) : enabled ? (
          // ── Enabled: status + self-service disable (code-gated) ──
          <>
            <SettingsToggleRow
              title="Authenticator app"
              desc="To turn 2FA off, enter a current code from your app (or a backup code)."
              control={!disableMode ? (
                <button type="button" onClick={() => { setDisableMode(true); setError(null); setCode('') }} className={S_BTN_DANGER_SM}>
                  Disable 2FA
                </button>
              ) : undefined}
            />
            {disableMode && (
              <form onSubmit={submitDisable} className="pb-[4px]">
                {settingsError}
                <label className={`${S_LABEL} mt-[4px]`}>Current code</label>
                {codeInput}
                <div className={S_FORMFOOT}>
                  <button type="button" onClick={() => { setDisableMode(false); setCode(''); setError(null) }} disabled={busy} className={S_BTN_GHOST}>
                    Cancel
                  </button>
                  <button type="submit" disabled={busy} className={S_BTN_DANGER}>
                    {busy ? 'Disabling…' : 'Disable 2FA'}
                  </button>
                </div>
              </form>
            )}
            <SettingsToggleRow
              title="Backup codes"
              desc="Shown once when you enabled 2FA — each works once if you lose your device."
            />
          </>
        ) : (
          // ── Disabled → offer enrolment ─────────────────────
          <>
            {settingsError}
            <SettingsToggleRow
              title="Authenticator app"
              desc="Add a second step at sign-in using an authenticator app. Recommended for extra account security."
              control={
                <button type="button" onClick={() => { void startEnrolment() }} disabled={busy} className={S_BTN_PRIMARY_SM}>
                  {busy ? 'Starting…' : 'Enable 2FA'}
                </button>
              }
            />
          </>
        )}
      </SettingsCard>
    )
  }

  return (
    <div className={cardClass}>
      {/* Own heading only in Settings — the standalone forced screen
          supplies its own centred heading above the panel. */}
      {!standalone && (
        <h2 className="text-xs uppercase tracking-widest text-casevo-brand mb-5">
          Two-factor authentication
        </h2>
      )}

      {statusLoading ? (
        <div className={`h-6 w-40 rounded bg-casevo-mint/30 animate-pulse ${centerBlock}`} />
      ) : mode === 'codes' ? (
        // ── One-time backup codes ──────────────────────────
        <div>
          <ActionBox tone="warn" className="mb-4 text-left" title="Save your backup codes now."
            message={<>Each code works once and lets you sign in if you lose your device. They will <strong className="font-semibold">not be shown again.</strong></>} />
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm text-casevo-forest mb-4">
            {backupCodes.map((c) => (
              <li key={c} className="px-3 py-2 bg-casevo-input border border-casevo-border rounded text-center tracking-widest">
                {c}
              </li>
            ))}
          </ul>
          <div className={`flex flex-wrap gap-3 ${rowJustify}`}>
            <button type="button" onClick={copyCodes}
              className="text-xs uppercase tracking-widest text-casevo-brand hover:text-casevo-mid transition-colors">
              {copied ? 'Copied' : 'Copy codes'}
            </button>
            <button type="button" onClick={downloadCodes}
              className="text-xs uppercase tracking-widest text-casevo-brand hover:text-casevo-mid transition-colors">
              Download .txt
            </button>
            <button type="button" onClick={() => { setMode('idle'); setBackupCodes([]); onComplete?.() }}
              className={`${standalone ? '' : 'ml-auto'} text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-forest transition-colors`}>
              Done
            </button>
          </div>
        </div>
      ) : mode === 'enrolling' ? (
        // ── QR + confirm ───────────────────────────────────
        <form onSubmit={confirmEnrolment}>
          {errorBanner}
          <p className="text-sm text-casevo-text-soft mb-4">
            Scan this QR code with an authenticator app (Google Authenticator,
            1Password, Authy…), then enter the 6-digit code it shows.
          </p>
          {qrDataUrl && (
            <img src={qrDataUrl} alt="2FA QR code"
              className={`w-44 h-44 mb-4 rounded border border-casevo-border bg-white p-2 ${centerBlock}`} />
          )}
          {secret && (
            <p className="text-xs text-casevo-text-soft mb-4">
              Can't scan? Enter this key manually:{' '}
              <span className="font-mono text-casevo-forest break-all">{secret}</span>
            </p>
          )}
          <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
            Verification code
          </label>
          <input
            autoFocus
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={busy}
            className={`w-full max-w-xs border border-casevo-border rounded-lg px-4 py-2.5 text-base bg-casevo-input text-casevo-forest tracking-widest focus:outline-none focus:border-casevo-mid disabled:opacity-60 mb-4 ${centerBlock}`}
          />
          <div className={`flex gap-3 items-center ${rowJustify}`}>
            <button type="submit" disabled={busy}
              className="bg-casevo-primary text-white text-sm px-5 py-2.5 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium">
              {busy ? 'Confirming…' : 'Confirm'}
            </button>
            <button type="button" onClick={cancelEnrolment} disabled={busy}
              className="text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-forest disabled:opacity-60">
              Cancel
            </button>
          </div>
        </form>
      ) : enabled ? (
        // ── Enabled: status + self-service disable (code-gated) ──
        <div>
          <p className="text-sm text-casevo-forest flex items-center gap-2 mb-3">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            Two-factor authentication is <strong className="font-medium">on</strong>.
          </p>
          {!disableMode ? (
            <button type="button" onClick={() => { setDisableMode(true); setError(null); setCode('') }}
              className="text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-danger transition-colors">
              Disable 2FA
            </button>
          ) : (
            <form onSubmit={submitDisable}>
              {errorBanner}
              <p className="text-sm text-casevo-text-soft mb-3">
                Enter a current code from your authenticator app (or a backup
                code) to turn 2FA off.
              </p>
              <input
                autoFocus
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                disabled={busy}
                className="w-full max-w-xs border border-casevo-border rounded-lg px-4 py-2.5 text-base bg-casevo-input text-casevo-forest tracking-widest focus:outline-none focus:border-casevo-mid disabled:opacity-60 mb-4"
              />
              <div className="flex gap-3">
                <button type="submit" disabled={busy}
                  className="bg-red-600 text-white text-sm px-5 py-2.5 rounded-lg hover:bg-red-700 transition-colors uppercase tracking-widest disabled:opacity-50 font-medium">
                  {busy ? 'Disabling…' : 'Disable 2FA'}
                </button>
                <button type="button" onClick={() => { setDisableMode(false); setCode(''); setError(null) }} disabled={busy}
                  className="text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-forest disabled:opacity-60">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        // ── Disabled → offer enrolment ─────────────────────
        <div>
          {errorBanner}
          <p className="text-sm text-casevo-text-soft mb-4">
            Add a second step at sign-in using an authenticator app. Recommended
            for extra account security.
          </p>
          <button type="button" onClick={() => { void startEnrolment() }} disabled={busy}
            className="bg-casevo-primary text-white text-sm px-5 py-2.5 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium">
            {busy ? 'Starting…' : 'Enable 2FA'}
          </button>
        </div>
      )}
    </div>
  )
}

export default TwoFactorPanel
